import { useEffect, useState, type ReactNode } from 'react';

import { api } from '../lib/api.js';
import { refreshOrganisations, useOrganisations } from '../lib/organisations.js';

import { AppHeader } from './AppHeader.js';
import { AppSidebar } from './AppSidebar.js';
import { WorkspaceBand } from './WorkspacePath.js';
import { CommandPaletteProvider } from './CommandPalette.js';
import { Sheet, SheetContent } from './ui/sheet.js';
import { Toaster } from './ui/sonner.js';

/**
 * App-Shell.
 *
 * Aufbau:
 * - Desktop (>= md): Sidebar links (256px / 64px collapsed) + Main rechts.
 * - Mobile (< md): Sidebar versteckt, oeffnet als Sheet ueber Hamburger im Header.
 *
 * Toaster (sonner) ist global eingebunden; jede Page kann `toast(...)` aufrufen.
 *
 * Command-Palette: `CommandPaletteProvider` umschliesst Header + Main, damit
 * der Topbar-Trigger `useCommandPalette()` verwenden kann und Cmd+K global
 * funktioniert. Die Palette selbst rendert sich, wenn `open === true`.
 *
 * A11y (MR 5):
 * - Skip-to-Content-Link ist erstes fokussierbares Element.
 * - `<main>` traegt `id="main-content"` und `tabIndex={-1}`, damit der
 *   Skip-Link den Fokus dorthin setzen kann.
 * - Toaster nutzt `aria-live="polite"` (Default von sonner).
 */
const SIDEBAR_HIDDEN_KEY = 'civiport.layout.sidebarHidden';

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  // Gliederung hier laden, nicht in der Navigation: die ist ausblendbar, Arbeitsbereich, Kopf und
  // Uebersicht brauchen den Baum aber immer.
  const orgs = useOrganisations();
  useEffect(() => {
    if (!orgs.loaded) void refreshOrganisations(api.listOrganisations).catch(() => undefined);
  }, [orgs.loaded]);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop: Sidebar komplett ausblenden via Burger. localStorage-persistiert.
  const [sidebarHidden, setSidebarHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_HIDDEN_KEY) === '1';
  });
  function toggleDesktopSidebar(): void {
    setSidebarHidden((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_HIDDEN_KEY, next ? '1' : '0');
      }
      return next;
    });
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      {/* Skip-to-content link — sichtbar nur bei Tastatur-Fokus. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        Zum Hauptinhalt springen
      </a>

      {/* Desktop sidebar — komplett ausblendbar via Burger im Header */}
      {!sidebarHidden ? (
        <div className="hidden md:block">
          <AppSidebar
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((v) => !v)}
          />
        </div>
      ) : null}

      {/* Mobile sidebar (Sheet) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 border-r-0 p-0">
          <AppSidebar
            variant="mobile"
            collapsed={false}
            onToggleCollapsed={() => undefined}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <CommandPaletteProvider>
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader
            onOpenMobileMenu={() => setMobileOpen(true)}
            onToggleDesktopSidebar={toggleDesktopSidebar}
          />
          <WorkspaceBand />
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-y-auto px-4 py-6 outline-none md:px-6"
          >
            {children}
          </main>
        </div>
      </CommandPaletteProvider>

      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
